/**
 * A multicam set's separate sound recording, as the players and the
 * transcript see it: timed like an angle, under a key no angle uses.
 */

import type { ReviewAngle } from './multicamReview';
import { RECORDING_KEY } from './useSyncedAngles';

export { RECORDING_KEY };

/** What the set (or its snapshot) stores about its recording. */
export interface SeparateAudio {
  videoId?: string | null;
  label?: string | null;
  videoName?: string | null;
  videoUrl?: string | null;
  durationMs?: number | null;
  timelineStartMs?: number | null;
  sourceInMs?: number | null;
  sourceOutMs?: number | null;
}

export const DEFAULT_RECORDING_LABEL = 'Separate audio';

/** The recording as a player angle, or null when the set has none (or no file). */
export const recordingAngle = (source: SeparateAudio | null | undefined): ReviewAngle | null =>
  source?.videoUrl
    ? {
        angleKey: RECORDING_KEY,
        label: source.label || DEFAULT_RECORDING_LABEL,
        videoName: source.videoName ?? null,
        videoUrl: source.videoUrl,
        durationMs: Number(source.durationMs) || 0,
        timelineStartMs: Math.max(0, Number(source.timelineStartMs) || 0),
        sourceInMs: Math.max(0, Number(source.sourceInMs) || 0),
        sourceOutMs: typeof source.sourceOutMs === 'number' ? source.sourceOutMs : null,
      }
    : null;

/** "Separate audio (Zoom H6)", or "Separate audio". */
export const recordingDisplayName = (source: { label?: string | null } | null | undefined): string => {
  const label = String(source?.label ?? '').trim();
  return label && label.toLowerCase() !== DEFAULT_RECORDING_LABEL.toLowerCase() ? `${DEFAULT_RECORDING_LABEL} (${label})` : DEFAULT_RECORDING_LABEL;
};

/** Mid-sentence: "the separate audio (Zoom H6)", or "the separate audio". */
export const recordingPhrase = (source: { label?: string | null } | null | undefined): string => {
  const label = String(source?.label ?? '').trim();
  return label && label.toLowerCase() !== DEFAULT_RECORDING_LABEL.toLowerCase()
    ? `the separate audio (${label})`
    : 'the separate audio';
};

/** Where a transcript of the set's sound comes from: the recording, else the sound angle. */
export const transcriptSourceOf = (
  audioSource: SeparateAudio | null | undefined,
  fallback: { videoId?: string | null; timelineStartMs?: number | null; sourceInMs?: number | null; sourceOutMs?: number | null } | null | undefined
): { videoId: string; placement: { timelineStartMs: number; sourceInMs: number; sourceOutMs: number | null } } | null => {
  const source = audioSource?.videoId ? audioSource : fallback;
  if (!source?.videoId) return null;
  return {
    videoId: String(source.videoId),
    placement: {
      timelineStartMs: Number(source.timelineStartMs) || 0,
      sourceInMs: Number(source.sourceInMs) || 0,
      sourceOutMs: typeof source.sourceOutMs === 'number' ? source.sourceOutMs : null,
    },
  };
};
